import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BodyTypeRow } from "app/containers/BodyType/types";
import { TireTypeRow } from "app/containers/TireType/types";

import {
  RegistrationFilterValue,
  RegistrationForm,
  RegistrationFormDropDownKey,
  RegistrationListMeta,
  RegistrationRow,
  RegistrationState,
  RegistrationUpdate,
} from "../types";
import {
  createFormDropDownState,
  initialRegistrationForm,
  initialState,
} from "./initialState";

export const sliceKey = "registrationState";

type RegistrationListSuccessPayload = {
  data: RegistrationRow[];
  meta: RegistrationListMeta;
};

type FormDropDownSuccessPayload =
  | { key: "tires"; data: TireTypeRow[]; meta: RegistrationListMeta }
  | { key: "bodyTypes"; data: BodyTypeRow[]; meta: RegistrationListMeta };

const emptyFormDropDowns = () => ({
  tires: createFormDropDownState<TireTypeRow>(),
  bodyTypes: createFormDropDownState<BodyTypeRow>(),
});

const applyDropDownPage = (
  state: RegistrationState,
  payload: FormDropDownSuccessPayload,
  append: boolean
) => {
  const { meta } = payload;
  if (payload.key === "tires") {
    const dropDown = state.formDropDowns.tires;
    dropDown.items = append ? dropDown.items.concat(payload.data) : payload.data;
    dropDown.meta = meta;
    dropDown.page = meta.currentPage;
    dropDown.loading = false;
    return;
  }

  const dropDown = state.formDropDowns.bodyTypes;
  dropDown.items = append ? dropDown.items.concat(payload.data) : payload.data;
  dropDown.meta = meta;
  dropDown.page = meta.currentPage;
  dropDown.loading = false;
};

const registrationSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getRegistrationList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getRegistrationListSuccess(
      state,
      action: PayloadAction<RegistrationListSuccessPayload>
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getRegistrationListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<RegistrationListSuccessPayload>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    setDriverCabinFilter(
      state,
      action: PayloadAction<RegistrationFilterValue>
    ) {
      state.driverCabinFilter = action.payload;
    },

    setAxlesMeasureFilter(
      state,
      action: PayloadAction<RegistrationFilterValue>
    ) {
      state.axlesMeasureFilter = action.payload;
    },

    setFrontLoadFilter(state, action: PayloadAction<RegistrationFilterValue>) {
      state.frontLoadFilter = action.payload;
    },

    setRearAxlesMeasureFilter(
      state,
      action: PayloadAction<RegistrationFilterValue>
    ) {
      state.rearAxlesMeasureFilter = action.payload;
    },

    setWeightFilter(state, action: PayloadAction<RegistrationFilterValue>) {
      state.weightFilter = action.payload;
    },

    setTireFilter(state, action: PayloadAction<RegistrationFilterValue>) {
      state.tireFilter = action.payload;
    },

    setDhTypeFilter(state, action: PayloadAction<RegistrationFilterValue>) {
      state.dhTypeFilter = action.payload;
    },

    setBodyTypeFilter(state, action: PayloadAction<RegistrationFilterValue>) {
      state.bodyTypeFilter = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialRegistrationForm;
      state.formDropDowns = emptyFormDropDowns();
    },

    getFormDropDownList(state) {
      const tirePageSize = state.formDropDowns.tires.perPage;
      const bodyPageSize = state.formDropDowns.bodyTypes.perPage;
      state.formDropDowns.tires = {
        ...createFormDropDownState<TireTypeRow>(),
        perPage: tirePageSize,
        loading: true,
      };
      state.formDropDowns.bodyTypes = {
        ...createFormDropDownState<BodyTypeRow>(),
        perPage: bodyPageSize,
        loading: true,
      };
    },

    getFormDropDownListSuccess(
      state,
      action: PayloadAction<FormDropDownSuccessPayload>
    ) {
      applyDropDownPage(state, action.payload, false);
    },

    getFormDropDownListError(
      state,
      action: PayloadAction<{
        key: RegistrationFormDropDownKey;
        message: string;
      }>
    ) {
      state.formDropDowns[action.payload.key].loading = false;
      state.error = action.payload.message;
    },

    getFormDropDownNextPage(
      state,
      action: PayloadAction<RegistrationFormDropDownKey>
    ) {
      const dropDown = state.formDropDowns[action.payload];
      if (
        dropDown.loading ||
        dropDown.meta.currentPage >= dropDown.meta.totalPages
      ) {
        return;
      }
      dropDown.loading = true;
      dropDown.page = dropDown.meta.currentPage + 1;
    },

    getFormDropDownNextPageSuccess(
      state,
      action: PayloadAction<FormDropDownSuccessPayload>
    ) {
      applyDropDownPage(state, action.payload, true);
    },

    getFormDropDownNextPageError(
      state,
      action: PayloadAction<{
        key: RegistrationFormDropDownKey;
        message: string;
      }>
    ) {
      state.formDropDowns[action.payload.key].loading = false;
      state.error = action.payload.message;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof RegistrationForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    resetForm(state) {
      state.form = initialRegistrationForm;
    },

    addRegistration(state) {
      state.saving = true;
      state.error = null;
    },

    addRegistrationSuccess(state) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialRegistrationForm;
      state.formDropDowns = emptyFormDropDowns();
      state.error = null;
    },

    addRegistrationError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    updateRegistration(state, _action: PayloadAction<RegistrationUpdate>) {
      state.saving = true;
      state.error = null;
    },

    updateRegistrationSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    updateRegistrationError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    deleteRegistration(state, _action: PayloadAction<number>) {
      state.saving = true;
      state.error = null;
    },

    deleteRegistrationSuccess(state, action: PayloadAction<number>) {
      state.saving = false;
      state.error = null;
      state.list = state.list.filter((item) => item.keyID !== action.payload);
    },

    deleteRegistrationError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
  },
});

export const { actions: registrationActions, reducer } = registrationSlice;
